import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';

/**
 * One button, four intents, three sizes.
 *
 * The intents are not decoration. `success` is reserved for acts that
 * genuinely complete something — approve, verify, mark fit, release — and
 * `danger` for acts that destroy or withdraw. Using either for an ordinary
 * save spends the signal, and then a revoke looks like a save, which is how a
 * certificate gets withdrawn by accident.
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'success' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const VARIANT: Record<Variant, string> = {
  primary:
    'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 '
    + 'disabled:bg-primary-300',
  secondary:
    'bg-white text-neutral-800 border border-neutral-300 hover:bg-neutral-50 '
    + 'active:bg-neutral-100 disabled:text-neutral-400 disabled:bg-neutral-50',
  ghost:
    'text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200 disabled:text-neutral-400',
  success:
    'bg-success-600 text-white hover:brightness-95 active:brightness-90 disabled:opacity-50',
  danger:
    'bg-danger-600 text-white hover:brightness-95 active:brightness-90 disabled:opacity-50',
};

// 40px is the standard control height, matched by Input and Select so a
// button beside a field lines up without either being nudged.
const SIZE: Record<Size, string> = {
  // 40px on a phone: 32px is a mouse target, and the row's own action —
  // Rebook, Open case — is the one a thumb has to hit.
  sm: 'h-8 max-md:h-10 px-3 text-small gap-1.5',
  md: 'h-10 px-4 text-body gap-2',
  lg: 'h-11 px-5 text-body-lg gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /** Rendered before the label at the size this button expects. */
  icon?: ReactNode;
  children: ReactNode;
}

export function Button({
  variant = 'primary', size = 'md', loading, icon, disabled, className, children, ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      // Disabled while loading, so a slow create cannot be submitted twice.
      // The API is idempotent, but the user should not have to rely on that.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-btn font-semibold',
        'transition-colors duration-hover disabled:cursor-not-allowed',
        // The ring is drawn outside the control so it survives a button
        // sitting flush against an input group.
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        'focus-visible:ring-offset-1',
        VARIANT[variant], SIZE[size], className,
      )}
    >
      {loading ? (
        <span
          aria-hidden
          className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : icon}
      {children}
    </button>
  );
}

/**
 * A square button carrying only an icon.
 *
 * `label` is required rather than optional: an icon with no accessible name is
 * an unlabelled control, and a screen reader announces it as "button".
 */
export function IconButton({
  label, size = 'md', variant = 'ghost', className, children, ...rest
}: Omit<ButtonProps, 'children' | 'icon'> & { label: string; children: ReactNode }) {
  return (
    <Button
      {...rest}
      variant={variant}
      size={size}
      aria-label={label}
      title={label}
      className={cn(size === 'sm' ? 'w-8 max-md:w-10 px-0' : size === 'lg' ? 'w-11 px-0' : 'w-10 px-0', className)}
    >
      {children}
    </Button>
  );
}
